import React from 'react';
import { Clock, LogOut, Calendar, AlertCircle } from 'lucide-react';

interface AccessDeniedOverlayProps {
  reason?: string;
  currentTime?: string;
  onLogout?: () => void;
}

export const AccessDeniedOverlay: React.FC<AccessDeniedOverlayProps> = ({
  reason = 'outside_allowed_hours',
  currentTime,
  onLogout,
}) => {
  // Convert UTC time to Brazilian time (BRT/BRST)
  const getBrazilianTime = (utcTimeStr?: string): string => {
    if (!utcTimeStr) return new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });

    try {
      // Parse the UTC time string (format: "2025-12-29 18:02:28")
      const utcDate = new Date(utcTimeStr.replace(' ', 'T') + 'Z'); // Add Z to indicate UTC

      // Convert to Brazilian time
      return utcDate.toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Error parsing time:', error);
      return utcTimeStr;
    }
  };

  // Portuguese error messages based on reason
  const getErrorMessage = () => {
    switch (reason) {
      case 'outside_allowed_hours':
        return {
          title: 'Acesso Fora do Horário Permitido',
          description: 'Você está tentando acessar o sistema fora do seu horário de trabalho configurado.',
          icon: <Clock className="w-10 h-10 text-red-500" strokeWidth={1.5} />,
        };
      case 'no_active_assignments':
        return {
          title: 'Sem Atribuições Ativas',
          description: 'Você não possui nenhuma atribuição ativa no momento.',
          icon: <AlertCircle className="w-10 h-10 text-red-500" strokeWidth={1.5} />,
        };
      case 'assignments_not_active':
        return {
          title: 'Atribuições Não Ativas',
          description: 'Suas atribuições não estão ativas no momento.',
          icon: <Calendar className="w-10 h-10 text-red-500" strokeWidth={1.5} />,
        };
      default:
        return {
          title: 'Acesso Negado',
          description: 'Você não tem permissão para acessar o sistema neste momento.',
          icon: <AlertCircle className="w-10 h-10 text-red-500" strokeWidth={1.5} />,
        };
    }
  };

  const errorInfo = getErrorMessage();
  const brazilianTime = getBrazilianTime(currentTime);

  const currentYear = new Date().getFullYear();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <div className="w-full max-w-md px-6">
        {/* Modern Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Dark header with logo */}
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black py-8 px-6">
            <div className="flex flex-col items-center">
              <div className="mb-3">
                {/* Shield-centred variant — see LoadingScreen for why not panther-logo.svg. */}
                <img
                  src="/panther-logo-mark-centered-white.svg"
                  alt="Panther Logo"
                  className="w-20 h-20 object-contain"
                />
              </div>
              <h2 className="text-2xl font-bold text-white tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                ACCESS
              </h2>
            </div>
          </div>

          {/* Red accent bar */}
          <div className="h-2 bg-gradient-to-r from-red-500 to-red-600"></div>

          {/* Content */}
          <div className="p-8">

            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="p-3 bg-red-50 rounded-full">
                {errorInfo.icon}
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-3">
              {errorInfo.title}
            </h1>

            {/* Description */}
            <p className="text-gray-600 text-center mb-6 leading-relaxed">
              {errorInfo.description}
            </p>

            {/* Current Time (if provided) - now in Brazilian time */}
            {currentTime && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex flex-col items-center gap-1">
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock className="w-3.5 h-3.5 mr-1.5" />
                    <span>Horário atual (Brasil)</span>
                  </div>
                  <span className="text-lg font-mono font-semibold text-gray-900">{brazilianTime}</span>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-blue-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-800">
                    <strong>O que fazer?</strong>
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    {reason === 'outside_allowed_hours'
                      ? 'Verifique seu horário de acesso permitido com o administrador do sistema e tente novamente durante seu horário de trabalho.'
                      : 'Entre em contato com o administrador do sistema para verificar suas permissões de acesso.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold rounded-lg hover:from-gray-900 hover:to-black transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <LogOut className="w-5 h-5" />
              <span>Sair do Sistema</span>
            </button>

            {/* Footer */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  <span className="font-semibold">Panther Segurança</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Todos os direitos reservados. 1995-{currentYear}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional help text */}
        <p className="text-center text-gray-400 text-sm mt-6">
          Código de erro: <span className="font-mono text-gray-300">{reason}</span>
        </p>
      </div>
    </div>
  );
};
